import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { debounce } from 'lodash';

import {
	BUTTON_TYPES,
	DIRECTIONS,
	EvComboboxOption,
	EvRadioGroup,
	EvSelectNew,
	EvTextInput,
	IEvButtonProps
} from '@evinced-private/ui-common';

import { SiteScannerPopup } from '../../../../components/common/site-scanner-popup/SiteScannerPopup';
import {
	COLLECTION_DESCRIPTION_ARIA_LABEL_ID,
	COLLECTION_NAME_ARIA_LABEL_ID,
	COLLECTION_TYPE_ARIA_LABEL_ID
} from '../../../../consts/PropertyCollection';
import {
	CollectionTypeLabels,
	CollectionTypes,
	ICollection
} from '../../../../interfaces/Collection';
import { IProperty } from '../../../../interfaces/Property';
import { useCollections } from '../../../../providers/collections/CollectionsProvider';
import { useUserTenant } from '../../../../providers/userTenantProvider/UserTenantProvider';
import {
	COLLECTION_FORM_FIELDS,
	COLLECTION_TYPE_ERROR_MESSAGE,
	formEmptyErrorState,
	formEmptyState,
	getLastEdited,
	IFormErrors,
	isFormValid,
	mapCollectionPropertyIdsToOptions,
	mapPropertiesToOptions,
	validateCollectionForm
} from '../../helpers/EditCollectionFormHelper';

import './EditCollectionButton.scss';

interface IEditCollectionButtonProps extends Omit<IEvButtonProps, 'children'> {
	properties: IProperty[];
	buttonChildren: React.ReactNode;
	collection?: ICollection;
	isEditMode?: boolean;
}

export const EditCollectionButton: FC<IEditCollectionButtonProps> = ({
	collection,
	isEditMode = false,
	properties,
	buttonChildren,
	type,
	accessibilityProps,
	loading
}) => {
	const { hasAdminPermissions } = useUserTenant();
	const { createCollection, editCollection, isCollectionUpdating } = useCollections();
	const isMountedRef = useRef<boolean>(true);

	const [name, setName] = useState(collection?.name ?? formEmptyState.name);
	const [collectionType, setCollectionType] = useState(
		collection?.type ?? formEmptyState.collectionType
	);
	const [selectedProperties, setSelectedProperties] = useState<EvComboboxOption[]>(
		collection
			? mapCollectionPropertyIdsToOptions(collection?.properties, properties)
			: formEmptyState.selectedProperties
	);
	const [description, setDescription] = useState(
		collection?.description ?? formEmptyState.description
	);
	const [formErrors, setFormErrors] = useState<IFormErrors>(formEmptyErrorState);

	const isAdminUser = useMemo(hasAdminPermissions, [hasAdminPermissions]);
	const propertyOptions = useMemo(() => mapPropertiesToOptions(properties), [properties]);
	const formValues = useMemo(
		() => ({
			name,
			collectionType,
			selectedProperties,
			description
		}),
		[name, collectionType, selectedProperties, description]
	);

	const debouncedValidation = useMemo(
		() =>
			debounce((values) => {
				if (isMountedRef.current) {
					setFormErrors(validateCollectionForm(values, isAdminUser));
				}
			}, 300),
		[isAdminUser]
	);

	const performCollectionAction = useCallback(
		async (onClose: () => void): Promise<void> => {
			if (!isMountedRef.current) {
				return;
			}

			const formErrors = validateCollectionForm(formValues, isAdminUser);

			setFormErrors(formErrors);

			if (!isFormValid(formErrors)) {
				return;
			}

			const data = {
				id: collection?.id ?? undefined,
				name: formValues.name,
				description: formValues.description,
				type: formValues.collectionType,
				properties: formValues.selectedProperties.map((property) => property.value.toString())
			};

			const safeOnClose = (): void => {
				if (isMountedRef.current) {
					onClose();
				}
			};

			if (isEditMode) {
				await editCollection(data, safeOnClose);
			} else {
				await createCollection(data, safeOnClose);
			}
		},
		[collection?.id, isEditMode, formValues, editCollection, createCollection, isAdminUser]
	);

	const buttons = useMemo(
		() => [
			{
				title: 'Cancel',
				type: BUTTON_TYPES.SECONDARY,
				onClick: (onClose: () => void) => onClose()
			},
			{
				title: isEditMode ? 'Save' : 'Create Collection',
				onClick: performCollectionAction,
				disabled: !isFormValid(formErrors)
			}
		],
		[formErrors, isEditMode, performCollectionAction]
	);

	const handleTypeChange = useCallback(
		(value: CollectionTypes): void => {
			if (isMountedRef.current) {
				setCollectionType(value);
				debouncedValidation({ ...formValues, collectionType: value });
			}
		},
		[formValues, debouncedValidation]
	);

	const radioGroupOptions = useMemo(
		() => [
			{
				label: CollectionTypeLabels.USER,
				isChecked: collectionType === CollectionTypes.USER,
				onClick: () => handleTypeChange(CollectionTypes.USER)
			},
			{
				label: CollectionTypeLabels.TENANT,
				isChecked: collectionType === CollectionTypes.TENANT,
				onClick: () => handleTypeChange(CollectionTypes.TENANT)
			}
		],
		[collectionType, handleTypeChange]
	);

	const handleNameChange = useCallback(
		(value: string): void => {
			if (isMountedRef.current) {
				setName(value);
				debouncedValidation({ ...formValues, name: value });
			}
		},
		[formValues, debouncedValidation]
	);

	const handlePropertiesChange = useCallback(
		(selectedProperties: EvComboboxOption[]): void => {
			if (isMountedRef.current) {
				setSelectedProperties(selectedProperties);
				debouncedValidation({ ...formValues, selectedProperties });
			}
		},
		[formValues, debouncedValidation]
	);

	const handleDescriptionChange = useCallback(
		(value: string): void => {
			if (isMountedRef.current) {
				setDescription(value);
				debouncedValidation({ ...formValues, description: value });
			}
		},
		[formValues, debouncedValidation]
	);

	const resetForm = (): void => {
		setName(collection?.name ?? formEmptyState.name);
		setCollectionType(collection?.type ?? formEmptyState.collectionType);
		setSelectedProperties(
			collection
				? mapCollectionPropertyIdsToOptions(collection.properties, properties)
				: formEmptyState.selectedProperties
		);
		setDescription(collection?.description ?? formEmptyState.description);
		setFormErrors(formEmptyErrorState);
	};

	const onClosePopup = (onClose?: () => void): void => {
		resetForm();
		onClose?.();
	};

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
			debouncedValidation.cancel();
		};
	}, [debouncedValidation]);

	const title = `${isEditMode ? 'Edit' : 'Create'} collection`;
	const lastEdited = getLastEdited(collection);

	return (
		<SiteScannerPopup
			buttons={buttons}
			isCentered
			isBgTransparent
			title={title}
			onClose={onClosePopup}
			triggerButtonProps={{
				accessibilityProps,
				title,
				type,
				children: buttonChildren,
				loading
			}}
			popupWidth={480}
			className="edit-collection-button"
			isPopupLoading={isCollectionUpdating}
		>
			<div className="collection-edit-form">
				<div className="form-field">
					<label id={COLLECTION_NAME_ARIA_LABEL_ID} aria-hidden>
						Collection name
					</label>
					<EvTextInput
						className="name-input"
						value={name}
						error={formErrors[COLLECTION_FORM_FIELDS.NAME][0] ?? ''}
						onChange={handleNameChange}
						ariaLabelledBy={COLLECTION_NAME_ARIA_LABEL_ID}
						placeholder="Enter name"
					/>
				</div>
				<div className="form-field">
					<EvRadioGroup
						className="collection-type-radio-group"
						options={radioGroupOptions}
						name="collection-type-radio"
						direction={DIRECTIONS.ROW}
						ariaLabelledBy={COLLECTION_TYPE_ARIA_LABEL_ID}
						ariaLabel="Collection type"
						title="Collection type"
						titleTooltip={COLLECTION_TYPE_ERROR_MESSAGE}
						isDisabled={!isAdminUser}
					/>
					<span className="input-error">
						{formErrors[COLLECTION_FORM_FIELDS.COLLECTION_TYPE][0] ?? ''}
					</span>
				</div>
				<div className="form-field">
					<EvSelectNew
						id="properties-select"
						label="Properties"
						placeholder="Select properties"
						options={propertyOptions}
						selectedValue={selectedProperties}
						onSelectChange={handlePropertiesChange}
						tooltipContent="These properties will be added to your collection"
						dataAutomationId="properties-select"
						isMultiple
						isSearchable
						maxDisplayedItems={1000}
					/>
				</div>
				<div className="form-field">
					<label id={COLLECTION_DESCRIPTION_ARIA_LABEL_ID} aria-hidden>
						Description (optional)
					</label>
					<textarea
						className={classNames('description-textarea', {
							'field-error': formErrors[COLLECTION_FORM_FIELDS.DESCRIPTION][0]
						})}
						value={description}
						onChange={(e) => handleDescriptionChange(e.target.value)}
						placeholder="Describe the purpose of this collection"
						aria-labelledby={COLLECTION_DESCRIPTION_ARIA_LABEL_ID}
						aria-invalid={!!formErrors[COLLECTION_FORM_FIELDS.DESCRIPTION][0]}
					/>
					<span className="input-error">
						{formErrors[COLLECTION_FORM_FIELDS.DESCRIPTION][0] ?? ''}
					</span>
				</div>
				<div className="last-edited-section">{lastEdited}</div>
			</div>
		</SiteScannerPopup>
	);
};
